import { useState } from 'react';
import { Plus, X, Calendar } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { post } from '@/lib/api';

interface TimeOffRequest {
  id: string;
  employee_name: string;
  time_off_type: string;
  start_date: string;
  end_date: string;
  hours: number;
  status: string;
  reason?: string;
}

export function TimeOffPage() {
  const [status, setStatus] = useState('pending');
  const { data, loading, refetch } = useApi<{ requests: TimeOffRequest[] }>(`time-off?status=${status}`, [status]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [requestForm, setRequestForm] = useState({
    employee_id: '',
    employee_name: '',
    time_off_type: 'vacation',
    start_date: '',
    end_date: '',
    hours: '',
    reason: '',
  });

  async function handleAction(id: string, action: 'approve' | 'deny') {
    await post(`time-off/${id}/${action}`, action === 'deny' ? { reason: 'Scheduling conflict' } : {});
    refetch();
  }

  async function handleCreateRequest() {
    if (!requestForm.start_date || !requestForm.end_date) {
      setCreateError('Start date and end date are required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await post('time-off', {
        employee_id: requestForm.employee_id || undefined,
        employee_name: requestForm.employee_name || undefined,
        time_off_type: requestForm.time_off_type,
        start_date: requestForm.start_date,
        end_date: requestForm.end_date,
        hours: requestForm.hours ? parseFloat(requestForm.hours) : undefined,
        reason: requestForm.reason || undefined,
      });
      setShowCreate(false);
      setRequestForm({ employee_id: '', employee_name: '', time_off_type: 'vacation', start_date: '', end_date: '', hours: '', reason: '' });
      refetch();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setCreating(false);
    }
  }

  const inputClass = 'w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-foreground">Time Off</h2>
        </div>
        <div className="flex items-center gap-3">
          <select value={status} onChange={e => setStatus(e.target.value)} className="bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
          </select>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
            <Plus className="w-4 h-4" /> Submit Request
          </button>
        </div>
      </div>
      {loading ? <div className="text-muted-foreground">Loading...</div> : (
        <DataTable
          columns={[
            { key: 'employee_name', header: 'Employee', render: (r: TimeOffRequest) => <span className="font-medium">{r.employee_name}</span> },
            { key: 'time_off_type', header: 'Type', render: (r: TimeOffRequest) => <span className="capitalize">{r.time_off_type.replace('_', ' ')}</span> },
            { key: 'start_date', header: 'Start', render: (r: TimeOffRequest) => r.start_date?.split('T')[0] ?? '' },
            { key: 'end_date', header: 'End', render: (r: TimeOffRequest) => r.end_date?.split('T')[0] ?? '' },
            { key: 'hours', header: 'Hours' },
            { key: 'status', header: 'Status', render: (r: TimeOffRequest) => <StatusBadge status={r.status} /> },
            { key: 'actions', header: '', render: (r: TimeOffRequest) => r.status === 'pending' ? (
              <div className="flex gap-2">
                <button onClick={(e) => { e.stopPropagation(); handleAction(r.id, 'approve'); }} className="px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded text-xs hover:bg-emerald-500/20">Approve</button>
                <button onClick={(e) => { e.stopPropagation(); handleAction(r.id, 'deny'); }} className="px-2 py-1 bg-red-500/10 text-red-400 rounded text-xs hover:bg-red-500/20">Deny</button>
              </div>
            ) : null },
          ]}
          data={data?.requests ?? []}
        />
      )}

      {/* Create Request Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Submit Time Off Request</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            {createError && <p className="text-red-400 text-sm">{createError}</p>}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Employee Name</label>
                <input value={requestForm.employee_name} onChange={e => setRequestForm(f => ({ ...f, employee_name: e.target.value }))} placeholder="John Smith" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Type</label>
                <select value={requestForm.time_off_type} onChange={e => setRequestForm(f => ({ ...f, time_off_type: e.target.value }))} className={inputClass}>
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal</option>
                  <option value="bereavement">Bereavement</option>
                  <option value="jury_duty">Jury Duty</option>
                  <option value="parental">Parental Leave</option>
                  <option value="unpaid">Unpaid Leave</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Start Date *</label>
                  <input type="date" value={requestForm.start_date} onChange={e => setRequestForm(f => ({ ...f, start_date: e.target.value }))} className={inputClass} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">End Date *</label>
                  <input type="date" value={requestForm.end_date} onChange={e => setRequestForm(f => ({ ...f, end_date: e.target.value }))} className={inputClass} />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Hours</label>
                <input type="number" value={requestForm.hours} onChange={e => setRequestForm(f => ({ ...f, hours: e.target.value }))} placeholder="8" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Reason</label>
                <textarea value={requestForm.reason} onChange={e => setRequestForm(f => ({ ...f, reason: e.target.value }))} placeholder="Optional reason..." rows={2} className={inputClass} />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
              <button onClick={() => void handleCreateRequest()} disabled={creating} className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50">
                {creating ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
